import {
  Datagrid,
  List,
  Create,
  Edit,
  SimpleForm,
  TextField,
  TextInput,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  SelectInput,
  Show,
  TabbedShowLayout,
  Tab,
  ReferenceManyField,
  DateField,
  ReferenceField,
  ReferenceArrayField,
  SingleFieldList,
  ChipField,
  FunctionField,
  usePermissions,
  useRecordContext,
} from 'react-admin';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { RequireStudents } from '../components/DependencyGate';
import { ArchivalToolbar } from '../components/ArchivalToolbar';
import { getFullName } from '../components/selects';
import { hasRole, type Permissions, type Team } from '../types';

const STATUS_CHOICES = [
  { id: 'active', name: 'Active' },
  { id: 'archived', name: 'Archived' },
];

const TeamListInner = () => {
  const { permissions } = usePermissions<Permissions>();
  return (
    <List
      exporter={false}
      sort={{ field: 'name', order: 'ASC' }}
      perPage={25}
      empty={
        <Box p={3}>
          <Typography>No teams yet.</Typography>
        </Box>
      }
    >
      <Datagrid rowClick={hasRole(permissions, 'tenant_admin') ? 'edit' : 'show'}>
        <TextField label="Team Name" source="name" />
        <FunctionField
          label="Members"
          render={(record: Team) => record.member_ids?.length ?? 0}
        />
        <FunctionField
          label="Status"
          render={(record: Team) => (
            <Chip
              label={record.status ?? 'active'}
              size="small"
              color={record.status === 'archived' ? 'default' : 'success'}
              variant="outlined"
            />
          )}
        />
      </Datagrid>
    </List>
  );
};

const TeamList = () => (
  <RequireStudents>
    <TeamListInner />
  </RequireStudents>
);

const TeamMemberChip = () => {
  const record = useRecordContext<any>();
  const label = [record?.first_name, record?.last_name].filter(Boolean).join(' ') || record?.id;
  if (label) return <Chip size="small" label={label} sx={{ mr: 0.5, mb: 0.5 }} />;
  return <ChipField source="id" size="small" />;
};

const TeamShow = () => (
  <Show>
    <TabbedShowLayout>
      <Tab label="Roster">
        <Stack spacing={1}>
          <TextField source="name" label="Team Name" />
          <FunctionField
            label="Status"
            render={(record: Team) => (
              <Chip
                label={record.status ?? 'active'}
                size="small"
                color={record.status === 'archived' ? 'default' : 'success'}
                variant="outlined"
              />
            )}
          />
          <DateField source="created_at" label="Created" showTime />
        </Stack>
        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="subtitle2">Members</Typography>
        </Box>
        <ReferenceArrayField label="" source="member_ids" reference="students">
          <SingleFieldList linkType="show">
            <TeamMemberChip />
          </SingleFieldList>
        </ReferenceArrayField>
      </Tab>
      <Tab label="Recent Attempts">
        <ReferenceManyField
          label=""
          reference="attempts"
          target="team_id"
          sort={{ field: 'attempted_at', order: 'DESC' }}
          perPage={25}
        >
          <Datagrid bulkActionButtons={false}>
            <ReferenceField label="Event" source="event_id" reference="events">
              <TextField source="name" />
            </ReferenceField>
            <ReferenceField label="Student" source="student_id" reference="students">
              <FunctionField render={(s: any) => [s.first_name, s.last_name].filter(Boolean).join(' ')} />
            </ReferenceField>
            <TextField source="attempt_index" label="#" />
            <TextField source="attempted_at" label="Date" />
          </Datagrid>
        </ReferenceManyField>
      </Tab>
    </TabbedShowLayout>
  </Show>
);

function minTwoMembers(value: unknown): string | undefined {
  if (!Array.isArray(value) || value.length < 2) return 'A team requires at least 2 members';
  return undefined;
}

const TeamCreate = () => (
  <Create>
    <TeamForm />
  </Create>
);

const TeamEdit = () => (
  <Edit mutationMode="pessimistic">
    <TeamForm />
  </Edit>
);

const TeamForm = () => (
  <SimpleForm toolbar={<ArchivalToolbar />} warnWhenUnsavedChanges>
    <TextInput fullWidth label="Team Name" source="name" />
    <ReferenceArrayInput
      perPage={200}
      label="Team Members (Students)"
      source="member_ids"
      reference="students"
      sort={{ field: 'first_name', order: 'ASC' }}
    >
      <AutocompleteArrayInput optionText={getFullName} validate={minTwoMembers} />
    </ReferenceArrayInput>
    <SelectInput
      fullWidth
      label="Status"
      source="status"
      choices={STATUS_CHOICES}
      defaultValue="active"
    />
  </SimpleForm>
);

export const teams = {
  list: TeamList,
  show: TeamShow,
  create: TeamCreate,
  edit: TeamEdit,
};
